import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Zap } from "lucide-react";

/**
 * Cross-cutting initiative spotlight — surfaces 3 joint workstreams on
 * every report so stakeholders see them regardless of which report they
 * land on:
 *  1. CCH Platform — Phase B (Discovery wrap-up, May–Jun → Build July)
 *  2. QR Menu — Joint with Operations · May–Jul (scalability & onboarding)
 *  3. Admin Panel — Discovery · PO to be defined
 *
 * Hidden in print (`no-print`) to keep PDF page counts stable (5/3/3).
 */
const INITIATIVES = [
    {
        n: "01",
        name: "CCH Platform",
        eyebrow: "Phase B · Discovery wrap-up",
        status: "Discovery",
        summary:
            "Three-layer architecture (BD Operations · HQ Governance · Customer Activation / myFORA). Closing Phase B in May–Jun before Build kicks off in July 2026.",
        phaseLabel: "May – Jun 2026",
        ownerLabel: "Doris Rozić Brajković · Karlo Duđerija · Robert Rozić",
        href: "/product-it#cch-platform",
        cta: "Read brief",
    },
    {
        n: "02",
        name: "QR Menu",
        eyebrow: "Joint with Operations · May – Jul",
        status: "In Progress",
        summary:
            "Mandatory for CCHBC expansion. Address scalability and onboarding bottlenecks — entry-level product for almost every venue and base for downstream rollout.",
        phaseLabel: "May – Jul 2026",
        ownerLabel: "Product & IT × Operations",
        href: "/operations#workstreams",
        cta: "Operations side",
    },
    {
        n: "03",
        name: "Admin Panel",
        eyebrow: "Cross-product back-office",
        status: "Discovery",
        summary:
            "Unified Admin Panel for cross-product back-office operations. Scope, ownership, and integration boundaries to be defined alongside CCH governance layer.",
        phaseLabel: "Discovery · Q2 2026",
        ownerLabel: "PO to be defined",
        href: "/product-it#priorities",
        cta: "View priorities",
        ownerPending: true,
    },
];

const StatusPillSmall = ({ status }) => {
    const map = {
        Discovery: "border-white/25 text-white/80",
        "In Progress": "border-[var(--fora-red-1)] text-[var(--fora-red-1)]",
        Live: "border-emerald-400/60 text-emerald-300",
    };
    const cls = map[status] || "border-white/25 text-white/80";
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 text-[9px] font-bold tracking-[0.18em] uppercase border ${cls}`}
        >
            {status}
        </span>
    );
};

const InitiativeSpotlight = () => (
    <section
        className="no-print bg-black text-white border-y border-white/10 relative overflow-hidden"
        data-testid="initiative-spotlight"
    >
        <div className="absolute inset-0 fora-pattern opacity-30 pointer-events-none" />
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-8 lg:py-10">
            {/* Header */}
            <div className="flex items-start gap-3 mb-6 lg:mb-8">
                <span
                    className="shrink-0 inline-flex items-center justify-center w-8 h-8 bg-[var(--fora-red-1)] text-white rounded-sm"
                    aria-hidden="true"
                >
                    <Zap className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                        Cross-cutting · Spotlight
                    </div>
                    <h3 className="mt-1 font-[800] text-lg sm:text-xl lg:text-2xl tracking-[-0.02em] leading-tight">
                        Three joint workstreams running across reports.
                    </h3>
                </div>
            </div>

            {/* 3-initiative grid */}
            <div
                className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5"
                data-testid="initiative-grid"
            >
                {INITIATIVES.map((it) => (
                    <article
                        key={it.n}
                        className="relative border border-white/15 bg-white/[0.03] p-4 lg:p-5 flex flex-col min-w-0 break-words hover:border-white/30 transition-colors"
                        data-testid={`initiative-card-${it.n}`}
                    >
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <span className="font-[800] text-2xl sm:text-3xl text-[var(--fora-red-1)] tracking-[-0.04em] leading-none">
                                {it.n}
                            </span>
                            <StatusPillSmall status={it.status} />
                        </div>

                        <h4 className="font-[800] text-base sm:text-lg tracking-[-0.02em] leading-tight">
                            {it.name}
                        </h4>
                        <div className="mt-1 text-[10px] font-bold tracking-[0.18em] uppercase text-[var(--fora-red-1)]">
                            {it.eyebrow}
                        </div>

                        <p className="mt-3 text-xs sm:text-[13px] text-white/70 leading-snug flex-1">
                            {it.summary}
                        </p>

                        <div className="mt-4 pt-3 border-t border-white/10 space-y-1.5">
                            <div className="flex items-start gap-2 text-[10px] tracking-[0.14em] uppercase font-semibold text-white/55">
                                <span className="shrink-0">Phase</span>
                                <span className="text-white/85 normal-case tracking-normal font-normal">
                                    {it.phaseLabel}
                                </span>
                            </div>
                            <div className="flex items-start gap-2 text-[10px] tracking-[0.14em] uppercase font-semibold text-white/55">
                                <span className="shrink-0">Owner</span>
                                <span
                                    className={`normal-case tracking-normal font-normal ${
                                        it.ownerPending
                                            ? "text-[var(--fora-red-1)] italic"
                                            : "text-white/85"
                                    }`}
                                >
                                    {it.ownerLabel}
                                </span>
                            </div>
                        </div>

                        <Link
                            to={it.href}
                            className="mt-4 inline-flex items-center justify-between gap-2 text-[10px] font-bold tracking-[0.18em] uppercase text-white hover:text-[var(--fora-red-1)] transition-colors group"
                            data-testid={`initiative-card-${it.n}-cta`}
                        >
                            <span>↗ {it.cta}</span>
                            <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform shrink-0" />
                        </Link>
                    </article>
                ))}
            </div>
        </div>
    </section>
);

export default InitiativeSpotlight;
export { INITIATIVES };
